// Diversification Optimizer PRO -- symbol -> deep-dive route resolver.
//
// Every ticker rendered in the PRO dashboard (notably the actionable rebalancing
// trade insights table) becomes a clickable link that opens the matching asset
// deep-dive page in a NEW browser tab:
//
//   equity -> /dashboard/stock-info-page?symbol=
//   etf    -> /dashboard/etf-info-page?symbol=
//   reit   -> /dashboard/reit-info-page?symbol=
//   crypto -> /dashboard/crypto-info-page?symbol=   (backend normalises BTC/BTCUSD)
//
// The optimizer payload carries no asset-class discriminant, so we resolve each
// symbol's class via the same platform-wide /global-search index that backs
// <GlobalSearchBar />. Resolution is async + cached process-wide; until a
// symbol resolves (or if it cannot be found) the link falls back to the equity
// deep-dive so it is ALWAYS a valid, immediately-clickable anchor.

import { useEffect, useMemo, useState } from "react";

import { fetchGlobalSearch, type AssetClass } from "../global-search";

// Deep-dive route per asset class -- mirrors <GlobalSearchBar /> / ScreenerPage
// so the routing contract stays identical across the SPA.
const ROUTE_BY_CLASS: Record<AssetClass, string> = {
  equity: "/dashboard/stock-info-page",
  etf: "/dashboard/etf-info-page",
  reit: "/dashboard/reit-info-page",
  crypto: "/dashboard/crypto-info-page",
};

// Safe fallback for an unresolved / unknown symbol: the equity deep-dive.
const DEFAULT_BASE_ROUTE = ROUTE_BY_CLASS.equity;

// Process-wide cache: normalised symbol -> resolved AssetClass (or null when
// the search index returned no confident match). Shared across every mount so
// re-running analyses never re-queries an already-known ticker.
const classCache = new Map<string, AssetClass | null>();

const normalise = (symbol: string): string => symbol.trim().toUpperCase();

/**
 * Build the absolute, query-param deep-dive href for a symbol given its
 * (optionally still-unknown) asset class.
 */
export function buildSymbolHref(
  symbol: string,
  assetClass: AssetClass | null | undefined,
): string {
  const base = (assetClass && ROUTE_BY_CLASS[assetClass]) || DEFAULT_BASE_ROUTE;
  return `${base}?symbol=${encodeURIComponent(symbol.trim())}`;
}

/**
 * Resolve a set of tickers to their deep-dive hrefs. Returns a map keyed by the
 * NORMALISED (trimmed, upper-cased) symbol. The map is seeded synchronously
 * (cache hits + equity fallbacks) and refined as the /global-search lookups
 * resolve, so consumers can render valid links on the very first paint.
 */
export function useSymbolRoutes(symbols: string[]): Record<string, string> {
  const [routes, setRoutes] = useState<Record<string, string>>({});

  // Stable primitive key so the effect only re-runs when the actual symbol
  // SET changes (not on every parent re-render / new array identity).
  const uniqueKey = useMemo(
    () =>
      Array.from(new Set(symbols.map(normalise).filter(Boolean)))
        .sort()
        .join(","),
    [symbols],
  );

  useEffect(() => {
    let cancelled = false;
    const unique = uniqueKey.length > 0 ? uniqueKey.split(",") : [];
    if (unique.length === 0) {
      setRoutes({});
      return;
    }

    const toHrefMap = (): Record<string, string> => {
      const map: Record<string, string> = {};
      unique.forEach((sym) => {
        map[sym] = buildSymbolHref(sym, classCache.get(sym) ?? null);
      });
      return map;
    };

    // 1. Seed immediately from the cache (unresolved symbols get the equity
    //    fallback) so links are clickable before any network round-trip.
    setRoutes(toHrefMap());

    // 2. Resolve only the symbols we have never seen before.
    const unresolved = unique.filter((sym) => !classCache.has(sym));
    if (unresolved.length === 0) return;

    void Promise.all(
      unresolved.map(async (sym) => {
        try {
          const results = await fetchGlobalSearch(sym, 8);
          const exact =
            results.find((r) => normalise(r.symbol) === sym) ??
            results.find((r) => normalise(r.symbol).startsWith(sym)) ??
            null;
          classCache.set(sym, exact ? exact.asset_class : null);
        } catch {
          // Degrade silently to the equity fallback.
          classCache.set(sym, null);
        }
      }),
    ).then(() => {
      if (!cancelled) setRoutes(toHrefMap());
    });

    return () => {
      cancelled = true;
    };
  }, [uniqueKey]);

  return routes;
}
